<template>
  <template v-if="loading && !$route.meta.noLoader">
    <div
      style="height: 100%"
      class="d-flex justify-content-center align-items-center tx-primary"
    >
      <div
        class="spinner-border"
        style="
          width: 70px;
          height: 70px;
          border-width: 6px;
          position: relative;
          top: -30px;
        "
      ></div>
    </div>
  </template>
  <template v-else>
    <header
      v-if="!$route.meta.hideHeader"
      :style="
        universalSettings?.forwarding &&
        universalSettings?.outgoing.outgoingUnverified &&
        justcallEnv
          ? 'height: 100px'
          : 'height: 60px'
      "
    >
      <NavBar />
    </header>
    <main
      :class="
        !$route.meta.hideHeader
          ? universalSettings?.forwarding &&
            universalSettings?.outgoing.outgoingUnverified &&
            justcallEnv
            ? 'main-with-header-banner'
            : 'main-with-header'
          : ''
      "
    >
      <RouterView :key="routeKey" />
    </main>

    <!-- Modal  -->
    <Teleport v-if="modal" to="body">
      <component :is="modal.component" v-bind="modal.props" />
    </Teleport>

    <!-- Drawer  -->
    <Teleport v-if="drawer" to="body">
      <component :is="drawer.component" v-bind="drawer.props" />
    </Teleport>
  </template>
</template>

<script setup lang="ts">
import * as Sentry from "@sentry/browser";
import { RouterView, useRoute } from "vue-router";
import { onBeforeMount, ref, watchEffect, computed } from "vue";
import { initFirebase } from "@/firebase";
import { useUserStore } from "@/stores/user";
import { useInboxStore } from "@/stores/inbox";
import { useRootStore } from "./stores/root";
import { storeToRefs } from "pinia";

import NavBar from "@/components/partials/NavBar/NavBar.vue";
import { useThreadStore } from "./stores/thread";
import { useTagStore } from "./stores/tag";
import dayjs from "dayjs";
import useShortcuts from "./functions/useShortcuts";
import mixpanel from "mixpanel-browser";

const route = useRoute();
const userStore = useUserStore();
const inboxStore = useInboxStore();
const threadStore = useThreadStore();
const rootStore = useRootStore();
const tagStore = useTagStore();
useShortcuts();

const { modal, drawer, initFirebasePromise, routeKey } = storeToRefs(rootStore);
const { user } = storeToRefs(userStore);
const { inboxes, inbox, isInboxUniversal, unifiedStats, inboxesMap } =
  storeToRefs(inboxStore);
const { threads } = storeToRefs(threadStore);
const { tags } = storeToRefs(tagStore);

const loading = ref(false);

const inboxId = computed(() => {
  return route.params.id as string;
});
const inboxData = computed(() => inboxesMap.value[inboxId.value]);
const universalSettings = computed(() => inboxData.value?.universalSettings);
const justcallEnv = import.meta.env.VITE_JUSTCALL_ENV;

watchEffect(() => {
  const threadId = route.params.threadId as string;
  const inboxId = route.params.id as string;
  const threadDetail = threads.value.find((t) => t.id.toString() === threadId);
  const tagId = route.query.tag;

  let pageTitleBase = "Helpwise";
  if (import.meta.env.VITE_JUSTCALL_ENV) {
    pageTitleBase = "JustCall";
  }

  document.title = pageTitleBase;
  // if user has open detail thread view
  if (threadId && threadDetail) {
    let threadTitle = "";
    if (threadDetail.inboxType === "mail") {
      // update head
      threadTitle = threadDetail?.subject;
    } else {
      threadTitle = threadDetail?.threadTitle;
    }
    if (threadTitle) {
      threadTitle = threadTitle + ` - ${pageTitleBase}`;
      document.title = threadTitle;
    }
  } else if (!threadId && !isInboxUniversal.value && inbox.value) {
    document.title =
      inbox.value.stats?.mine && inbox.value.stats?.mine != "0"
        ? `${inbox.value.displayName} (${inbox.value.stats.mine}) - ${pageTitleBase}`
        : inbox.value.displayName + " - Helpwise";
  } else if (tagId) {
    const foundTag = tags.value?.find((t) => t.id.toString() === tagId);
    if (foundTag) {
      document.title = foundTag.name + ` - ${pageTitleBase}`;
    }
  } else if (!threadId && isInboxUniversal.value && inboxId === "me") {
    document.title =
      unifiedStats?.value?.mine && unifiedStats?.value?.mine != "0"
        ? `Inbox (${unifiedStats?.value?.mine}) - ${pageTitleBase}`
        : `Inbox - ${pageTitleBase}`;
  } else if (!threadId && isInboxUniversal.value && inboxId !== "me") {
    const inbox = inboxes.value.find((i) => i.id.toString() === inboxId);

    if (inbox) {
      document.title =
        inbox.stats?.mine && inbox.stats?.mine != "0"
          ? `${inbox.displayName} (${inbox.stats.mine}) - ${pageTitleBase}`
          : inbox.displayName + ` - ${pageTitleBase}`;
    }
  }
});

onBeforeMount(async () => {
  loading.value = true;
  loadFavicon();

  const isIntegrationIframe = location.href.includes("/iframes/integrations");

  if (!isIntegrationIframe) {
    await userStore.fetchUserInfo();
    if (!import.meta.env.VITE_JUSTCALL_ENV) {
      await userStore.fetchFeatureFlags();
    }
    await inboxStore.fetchInboxes(false);
    inboxStore.fetchInboxes(true);
    rootStore.fetchNotification();
    initFirebasePromise.value = initFirebase();
  }

  if (user.value) {
    if (
      import.meta.env.MODE == "production" ||
      import.meta.env.MODE == "staging"
    ) {
      // Set sentry user
      Sentry.setUser({
        username: user.value?.firstname + " " + user.value?.lastname,
        id: user.value?.id.toString(),
        email: user.value?.email,
      });
    }
    // Google tag
    window.dataLayer = [];
    window.dataLayer.push({
      userId: user.value?.id - 204376,
    });
  }
  loading.value = false;
  if (!route.meta.noScripts) {
    if (!import.meta.env.VITE_JUSTCALL_ENV) {
      loadDelightedNps4Script();
      loadChatScript();
      loadAppCues();
      loadStatusPage();
      loadMixPanelIdentity();
      loadUserFlowScript();
    } else {
      loadIntercomScript();
      loadUserFlowScript();
      loadSmartlook();
    }
    if (import.meta.env.VITE_DROPBOX_APP_KEY) {
      loadDropboxScript();
    }
    if (
      import.meta.env.VITE_GOOGLE_DRIVE_CLIENT_ID &&
      import.meta.env.VITE_GOOGLE_DRIVE_DEVELOPER_KEY
    ) {
      loadGoogleDriveScript();
    }
    loadEasyCalendarScript();
    if (!route.query["skipOneSignalRedirection"]) {
      openPortal();
    }
  }
});

const loadMixPanelIdentity = () => {
  if (import.meta.env.VITE_MIX_PANEL_TOKEN) {
    mixpanel.identify(((user.value?.accountID ?? 0) - 204376).toString());
  }
};

const loadFavicon = () => {
  const link = document.createElement("link");
  link.rel = "icon";
  if (import.meta.env.VITE_JUSTCALL_ENV) {
    link.href = "https://cdn.justcall.io/app/assets/img/logo-fav.png";
  } else {
    link.href = "https://cdn.helpwise.io/assets/images/favicon.png";
  }
  document.head.appendChild(link);
};

const loadDelightedNps4Script = () => {
  if (!user.value || import.meta.env.MODE !== "production") return;
  const script = document.createElement("script");
  script.innerHTML = `      !(function (e, t, r, n) {
        if (!e[n]) {
          for (
            var a = (e[n] = []),
              i = [
                "survey",
                "reset",
                "config",
                "init",
                "set",
                "get",
                "event",
                "identify",
                "track",
                "page",
                "screen",
                "group",
                "alias",
              ],
              s = 0;
            s < i.length;
            s++
          ) {
            var c = i[s];
            a[c] =
              a[c] ||
              (function (e) {
                return function () {
                  var t = Array.prototype.slice.call(arguments);
                  a.push([e, t]);
                };
              })(c);
          }
          a.SNIPPET_VERSION = "1.0.1";
          var o = t.createElement("script");
          (o.type = "text/javascript"),
            (o.async = !0),
            (o.src =
              "https://d2yyd1h5u9mauk.cloudfront.net/integrations/web/v1/library/" +
              r +
              "/" +
              n +
              ".js");
          var p = t.getElementsByTagName("script")[0];
          p.parentNode.insertBefore(o, p);
        }
      })(window, document, "DaNafPTw9xNpdJp1", "delightedNps4");`;
  document.head.appendChild(script);
  if (dayjs(user.value?.joinDate).diff(dayjs(), "days") > 7) {
    window.delightedNps4.survey({
      email: user.value?.email,
      name: user.value?.firstname,
      createdAt: user.value?.joinDate,
      properties: {
        plan: user.value?.base_plan,
      },
    });
  }
};

const loadAppCues = () => {
  if (!user.value) return;
  const script1 = document.createElement("script");
  script1.innerHTML = `window.AppcuesSettings = {
        enableURLDetection: true,
      };`;
  document.head.appendChild(script1);
  const script2 = document.createElement("script");
  script2.setAttribute("src", "//fast.appcues.com/113934.js");
  document.head.appendChild(script2);
  script2.addEventListener("load", () => {
    window.Appcues &&
      window.Appcues.identify(user.value?.id ?? 0, {
        email: user.value?.email,
        firstname: user.value?.firstname,
        lastname: user.value?.lastname,
        plan: user.value?.base_plan,
        manager_id: user.value?.accountID ?? 0,
        stage: import.meta.env.MODE === "production" ? "prod" : "staging",
        created_at: dayjs(user.value?.joinDate).toISOString(),
        role: user.value?.role,
      });
  });
};

const loadSmartlook = () => {
  if (!user.value) return;
  let smartlookToken = "a8ad59b74b9a87d38f3b3e7b723571fda921fede";
  if (import.meta.env.MODE === "production") {
    if (import.meta.env.VITE_JUSTCALL_ENV) {
      smartlookToken = "af2f1392ce5809d7cbc19de9e8bdf5bd1e146cf7";
    }
    const script1 = document.createElement("script");
    script1.type = "text/javascript";
    /* eslint-disable */
    script1.innerHTML = `window.smartlook||(function(d) {
        var o=smartlook=function(){ o.api.push(arguments)},h=d.getElementsByTagName('head')[0];
        var c=d.createElement('script');o.api=new Array();c.async=true;c.type='text/javascript';
        c.charset='utf-8';c.src='https://web-sdk.smartlook.com/recorder.js';h.appendChild(c);
        })(document);
        smartlook('init', '${smartlookToken}', { region: 'eu' });`;
    document.body.appendChild(script1);
    window.smartlook &&
      window.smartlook("identify", user?.value.id, {
        email: user?.value.email,
        firstname: user?.value.firstname,
        lastname: user?.value.lastname,
        plan: user?.value.base_plan,
        managerId: user?.value.manager_id,
        stage: import.meta.env.MODE,
        role: user?.value.role,
        createdAt: dayjs(user?.value.joinDate).toISOString(),
      });
  }
};

const loadDropboxScript = () => {
  const script = document.createElement("script");
  script.setAttribute("src", "https://www.dropbox.com/static/api/2/dropins.js");
  script.setAttribute("id", "dropboxjs");
  script.setAttribute(
    "data-app-key",
    import.meta.env.VITE_DROPBOX_APP_KEY ?? ""
  );
  document.body.appendChild(script);
};

const loadGoogleDriveScript = () => {
  const script1 = document.createElement("script");
  script1.setAttribute("src", "https://apis.google.com/js/api.js");
  document.body.appendChild(script1);
  const script2 = document.createElement("script");
  script2.setAttribute("src", "https://accounts.google.com/gsi/client");
  document.body.appendChild(script2);
};

const loadUserFlowScript = () => {
  if (!user.value) return;
  const script1 = document.createElement("script");
  script1.type = "text/javascript";
  /* eslint-disable */
  script1.innerHTML = `
  !function () { var e = "undefined" == typeof window ? {} : window, r = e.userflow; if (!r) { var t = "https://js.userflow.com/", n = null; r = e.userflow = { _stubbed: !0, load: function () { return n || (n = new Promise((function (r, o) { var s = document.createElement("script"); s.async = !0; var a = e.USERFLOWJS_ENV_VARS || {}; "es2020" === (a.USERFLOWJS_BROWSER_TARGET || function (e) { for (var r = [[/Edg\\//, /Edg\\/(\d+)/, 80], [/OPR\\//, /OPR\\/(\d+)/, 67], [/Chrome\\//, /Chrome\\/(\d+)/, 80], [/CriOS\\//, /CriOS\\/(\d+)/, 100], [/Safari\\//, /Version\\/(\d+)/, 14], [/Firefox\\//, /Firefox\\/(\d+)/, 74]], t = 0; t < r.length; t++) { var n = r[t], o = n[0], s = n[1], a = n[2]; if (e.match(o)) { var l = e.match(new RegExp(s)); if (l && parseInt(l[1], 10) >= a) return "es2020"; break } } return "legacy" }(navigator.userAgent)) ? (s.type = "module", s.src = a.USERFLOWJS_ES2020_URL || t + "es2020/userflow.js") : s.src = a.USERFLOWJS_LEGACY_URL || t + "legacy/userflow.js", s.onload = function () { r() }, s.onerror = function () { document.head.removeChild(s), n = null; var e = new Error("Could not load Userflow.js"); console.error(e.message), o(e) }, document.head.appendChild(s) }))), n } }; var o = e.USERFLOWJS_QUEUE = e.USERFLOWJS_QUEUE || [], s = function (e) { r[e] = function () { var t = Array.prototype.slice.call(arguments); r.load(), o.push([e, null, t]) } }, a = function (e) { r[e] = function () { var t, n = Array.prototype.slice.call(arguments); r.load(); var s = new Promise((function (e, r) { t = { resolve: e, reject: r } })); return o.push([e, t, n]), s } }, l = function (e, t) { r[e] = function () { return t } }; s("_setTargetEnv"), s("closeResourceCenter"), s("disableEvalJs"), s("init"), s("off"), s("on"), s("prepareAudio"), s("registerCustomInput"), s("remount"), s("reset"), s("setBaseZIndex"), s("setCustomInputSelector"), s("setCustomNavigate"), s("setCustomScrollIntoView"), s("setInferenceAttributeFilter"), s("setInferenceAttributeNames"), s("setInferenceClassNameFilter"), s("setResourceCenterLauncherHidden"), s("setScrollPadding"), s("setServerEndpoint"), s("setShadowDomEnabled"), s("setPageTrackingDisabled"), s("setUrlFilter"), s("openResourceCenter"), s("toggleResourceCenter"), a("endAll"), a("endAllFlows"), a("endChecklist"), a("group"), a("identify"), a("identifyAnonymous"), a("start"), a("startFlow"), a("startWalk"), a("track"), a("updateGroup"), a("updateUser"), l("getResourceCenterState", null), l("isIdentified", !1) } }();
  `;
  /* eslint-disable */
  document.body.appendChild(script1);
  window.userflow.init(import.meta.env.VITE_USERFLOW_TOKEN);
  const userflowObj: Record<string, any> = {
    email: user.value?.email,
    name: user.value?.firstname + " " + user.value?.lastname,
    signed_up_at: dayjs(user.value?.joinDate).toISOString(),
    manager_id: user.value?.accountID - 204376,
  };
  if (!import.meta.env.VITE_JUSTCALL_ENV) {
    userflowObj.plan = user.value?.base_plan;
  }
  window.userflow.identify(user.value?.id - 204376, userflowObj);
  window.userflow.group(
    user.value?.accountID - 204376,
    {
      plan: user?.value.base_plan,
    },
    {
      membership: {
        role: user.value?.role,
      },
    }
  );
};

const loadStatusPage = () => {
  const script = document.createElement("script");
  script.setAttribute(
    "src",
    "https://fp5zqkhxg8c3.statuspage.io/embed/script.js"
  );
  document.body.appendChild(script);
};

const loadChatScript = () => {
  if (!user.value || user.value.mm) return;

  let widgetID = "62ac260c00e97";
  let widgetStage = "dev";
  if (import.meta.env.MODE == "staging") {
    widgetID = "62a86859a3a78";
    widgetStage = "staging";
  } else if (import.meta.env.MODE === "production") {
    widgetID = "5f7d595005e7a";
    widgetStage = "prod";
  }

  const script = document.createElement("script");

  window.helpwiseSettings = {
    widget_id: widgetID,
    align: "left",
    user_id: user.value?.id - 204376,
    email: user.value?.email,
    firstname: user.value?.firstname,
    lastname: user.value?.lastname,
    plan: user.value?.base_plan,
    manager_id: user.value?.accountID - 204376,
    STAGE: widgetStage,
  };

  script.src = "https://cdn2.helpwise.io/js/livechat.js";

  document.body.appendChild(script);
};

const loadIntercomScript = () => {
  if (!user.value || user.value.mm) return;

  const script = document.createElement("script");

  window.intercomSettings = {
    app_id: "zrssgyfk",
    name: user.value?.firstname + " " + user.value?.lastname,
    user_id: user.value?.id - 204376,
    email: user.value?.email,
    manager_id: user.value?.accountID - 204376,
    user_hash: user.value?.intercomHash,
  };
  script.innerHTML = `(function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',intercomSettings);}else{var d=document;var i=function(){i.c(arguments)};i.q=[];i.c=function(args){i.q.push(args)};w.Intercom=i;function l(){var s=d.createElement('script');s.type='text/javascript';s.defer=true;s.src='https://widget.intercom.io/widget/zrssgyfk';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);}if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})()`;
  document.body.appendChild(script);
};

const openPortal = () => {
  const currentTime = new Date();
  const time = currentTime.getTime();
  const timePlus7 = currentTime.getTime() + 7 * 24 * 60 * 60 * 1000;
  const getDateTime = localStorage.getItem("checkedOneSignal");
  if (getDateTime == null || time > Number(getDateTime)) {
    localStorage.setItem("checkedOneSignal", timePlus7.toString());
    window.location.href = `${
      import.meta.env.VITE_BASE_URL
    }subscribe?redirectUri=${location.href}`;
  }
};

const loadEasyCalendarScript = () => {
  let easyCalendarDemoSlug = "helpwise-demo-v2-lac";
  if (import.meta.env.VITE_JUSTCALL_ENV) {
    easyCalendarDemoSlug = "jc-email-demo";
  }
  const script = document.createElement("script");
  script.type = "text/javascript";
  script.src = "https://easycalendar.com/widget.js";
  script.async = true;
  script.onload = () => {
    window.easycalendar.init({
      classname: "bookdemoeasycalendar",
      type: "popup",
      color: "#ffffff",
      bgcolor: "#38a1f7",
      text: "Book an appointment",
      position: "left",
      primarycolor: "38a1f7",
    });
  };
  script.id = "easycalendar-calendar-widget";
  script.setAttribute("data-link", `https://cal.so/${easyCalendarDemoSlug}`);

  document.body.appendChild(script);
};
</script>

<style>
@import "@/assets/base.css";

.main-with-header {
  height: calc(100% - 60px);
}

.main-with-header-banner {
  height: calc(100% - 100px);
}
</style>
